import { useMemo, useState, type FormEvent } from "react";
import { ChevronRight, ScanBarcode, X } from "lucide-react";
import type { PricebookItem } from "@clevis/shared-types";
import { Button, Card, Select, cn } from "@clevis/shared-ui";
import { BulkBar, ListFooter, SortHeader, useListView } from "./ListControls";
import { CameraScanButton } from "./ScanTools";

// Inventory as a working list. The box on top takes a scanner (a keyboard-
// wedge scanner types the SKU and presses Enter: an exact hit opens the item)
// or a search. Show tracked / low / not yet tracked / everything; tick
// untracked items and "Start tracking" to begin counting them.

export type InventoryRow = PricebookItem & { id: string };
const isLow = (i: InventoryRow) => i.trackInventory === true && i.reorderLevel != null && (i.onHandQty ?? 0) <= i.reorderLevel;

export function InventoryList({
  items,
  selectedId,
  onSelect,
  onScanMiss,
  setTracking,
  onPrintLabels,
  say,
}: {
  items: InventoryRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onScanMiss: (code: string) => void;
  setTracking: (ids: string[], track: boolean) => Promise<void>;
  /** Print QR labels for the ticked items. */
  onPrintLabels?: (rows: InventoryRow[]) => void;
  say: (m: string) => void;
}) {
  const trackedCount = items.filter((i) => i.trackInventory).length;
  const [show, setShow] = useState<"tracked" | "low" | "untracked" | "all">(() => (trackedCount ? "tracked" : "all"));
  const [category, setCategory] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const categories = useMemo(() => [...new Set(items.map((i) => i.category.trim()).filter(Boolean))].sort(), [items]);

  const view = useListView(items, {
    text: (i) => [i.name, i.sku, i.category, i.bin],
    sorts: {
      name: (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }),
      sku: (a, b) => (a.sku || "~").localeCompare(b.sku || "~"),
      bin: (a, b) => (a.bin || "~").localeCompare(b.bin || "~", undefined, { numeric: true }),
      onhand: (a, b) => (a.trackInventory ? (a.onHandQty ?? 0) : -1) - (b.trackInventory ? (b.onHandQty ?? 0) : -1),
      reorder: (a, b) => (a.reorderLevel ?? -1) - (b.reorderLevel ?? -1),
    },
    defaultSort: "name",
    filter: (i) =>
      (show === "all" || (show === "tracked" ? i.trackInventory === true : show === "low" ? isLow(i) : i.trackInventory !== true)) &&
      (!category || i.category.trim() === category),
  });
  const allPicked = view.page.length > 0 && view.page.every((i) => picked.has(i.id));
  const pickedRows = items.filter((i) => picked.has(i.id));
  const untrackedHits = show !== "all" && view.query.trim() ? items.filter((i) => [i.name, i.sku, i.category, i.bin].join(" ").toLowerCase().includes(view.query.trim().toLowerCase())).length - view.filtered.length : 0;

  /** A part number, or a Clevis label's "item:<id>". */
  function findCode(raw: string) {
    const code = raw.trim().toLowerCase();
    if (!code) return;
    const hit = code.startsWith("item:") ? items.find((i) => i.id.toLowerCase() === code.slice(5)) : items.find((i) => (i.sku ?? "").trim().toLowerCase() === code);
    if (hit) {
      onSelect(hit.id);
      say(`Scanned: ${hit.name}`);
      view.setQuery("");
    } else {
      view.setQuery(raw.trim());
      onScanMiss(code);
    }
  }
  function onScan(e: FormEvent) {
    e.preventDefault();
    findCode(view.query);
  }
  async function track(on: boolean) {
    const ids = [...picked];
    try {
      await setTracking(ids, on);
      say(`${on ? "Now tracking" : "Stopped tracking"} ${ids.length} item${ids.length === 1 ? "" : "s"}.${on ? " Open each to set its count." : ""}`);
      setPicked(new Set());
    } catch (err) {
      say((err as Error).message);
    }
  }
  const cols = "sm:grid-cols-[24px_minmax(0,2.6fr)_minmax(0,1fr)_minmax(0,0.8fr)_80px_96px_16px]";

  return (
    <Card className="p-0">
      <div className="flex flex-wrap items-center gap-2 border-b border-steel/10 p-3">
        <form onSubmit={onScan} className="relative flex min-w-56 flex-1 items-center">
          <ScanBarcode size={15} className="pointer-events-none absolute left-2.5 text-steel" />
          <input
            className="w-full rounded-lg border border-steel/40 bg-white py-1.5 pl-8 pr-7 text-base text-ink placeholder:text-steel focus:border-brandorange focus:outline-none focus:ring-1 focus:ring-brandorange sm:text-sm"
            placeholder="Scan a SKU, or search name, bin, category…"
            value={view.query}
            onChange={(e) => view.setQuery(e.target.value)}
            autoFocus
          />
          {view.query ? (
            <button type="button" aria-label="Clear" className="absolute right-2 text-steel hover:text-ink" onClick={() => view.setQuery("")}>
              <X size={14} />
            </button>
          ) : null}
        </form>
        <CameraScanButton onCode={findCode} />
        <Select
          className="w-auto py-1.5 text-sm"
          aria-label="Show"
          value={show}
          onChange={(e) => {
            setShow(e.target.value as typeof show);
            setPicked(new Set());
          }}
        >
          <option value="tracked">Tracked ({trackedCount})</option>
          <option value="low">Below reorder ({items.filter(isLow).length})</option>
          <option value="untracked">Not tracked yet ({items.length - trackedCount})</option>
          <option value="all">All items ({items.length})</option>
        </Select>
        {categories.length ? (
          <Select className="w-auto py-1.5 text-sm" aria-label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        ) : null}
      </div>

      <BulkBar count={picked.size} onClear={() => setPicked(new Set())}>
        {pickedRows.some((i) => !i.trackInventory) ? (
          <Button variant="primary" className="px-2.5 py-1 text-xs" onClick={() => void track(true)}>
            Start tracking
          </Button>
        ) : null}
        {pickedRows.some((i) => i.trackInventory) ? (
          <Button variant="secondary" className="border border-white/30 px-2.5 py-1 text-xs" onClick={() => void track(false)}>
            Stop tracking
          </Button>
        ) : null}
        {onPrintLabels ? (
          <Button variant="secondary" className="border border-white/30 px-2.5 py-1 text-xs" onClick={() => onPrintLabels(pickedRows)}>
            Print QR labels
          </Button>
        ) : null}
      </BulkBar>

      <div className={cn("hidden items-center gap-3 border-b border-steel/10 px-4 py-2 sm:grid", cols)}>
        <input
          type="checkbox"
          aria-label="Select this page"
          checked={allPicked}
          onChange={() =>
            setPicked((p) => {
              const n = new Set(p);
              for (const i of view.page) {
                if (allPicked) n.delete(i.id);
                else n.add(i.id);
              }
              return n;
            })
          }
        />
        <SortHeader label="Item" k="name" view={view} />
        <SortHeader label="SKU" k="sku" view={view} />
        <SortHeader label="Bin" k="bin" view={view} />
        <SortHeader label="On hand" k="onhand" view={view} className="justify-end" />
        <SortHeader label="Reorder at" k="reorder" view={view} className="justify-end" />
        <span />
      </div>

      {view.page.map((i) => (
        <div
          key={i.id}
          className={cn(
            "grid cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-x-3 border-b border-steel/10 px-4 py-1.5 text-sm last:border-0 hover:bg-cream/60 sm:gap-3",
            cols,
            selectedId === i.id && "bg-brandorange/5",
          )}
          onClick={() => onSelect(i.id)}
        >
          <input
            type="checkbox"
            aria-label={`Select ${i.name}`}
            checked={picked.has(i.id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() =>
              setPicked((p) => {
                const n = new Set(p);
                if (n.has(i.id)) n.delete(i.id);
                else n.add(i.id);
                return n;
              })
            }
          />
          <span className="min-w-0">
            <span className="block truncate font-medium text-ink">{i.name}</span>
            <span className="block truncate text-[11px] text-steel">
              {i.category || "Uncategorized"}
              <span className="sm:hidden">{[i.sku, i.bin ? `bin ${i.bin}` : ""].filter(Boolean).map((x) => ` · ${x}`).join("")}</span>
            </span>
          </span>
          <span className="hidden truncate font-mono text-xs text-steel sm:block">{i.sku || "—"}</span>
          <span className="hidden truncate text-steel sm:block">{i.bin || "—"}</span>
          <span className="text-right">
            {i.trackInventory ? (
              <span className={cn("font-semibold", isLow(i) ? "text-danger" : "text-ink")}>{i.onHandQty ?? 0}</span>
            ) : (
              <span className="text-xs text-steel">not tracked</span>
            )}
          </span>
          <span className="hidden text-right text-steel sm:block">{i.reorderLevel ?? "—"}</span>
          <ChevronRight size={14} className="hidden text-steel sm:block" />
        </div>
      ))}
      {view.filtered.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-steel">
          {show === "tracked" && trackedCount === 0
            ? "Nothing tracked yet. Show “Not tracked yet”, tick the parts you stock, and Start tracking."
            : "Nothing matches."}
          {untrackedHits > 0 ? (
            <button className="ml-1 text-brandorange-dark underline" onClick={() => setShow("all")}>
              {untrackedHits} more in all items
            </button>
          ) : null}
        </div>
      ) : null}
      <ListFooter shown={view.page.length} matching={view.filtered.length} total={view.total} onMore={view.more} onAll={view.all} noun="items" />
    </Card>
  );
}
